import type { PartnerViewModel } from '@trinnvis/dabih-api-client';
import { BASE_PATH } from '@trinnvis/dabih-api-client';
import {
  computersNotDeleted,
  contactsNotDeleted,
  contractsNotDeleted,
  currentEmployeeUuid,
  getContactsAsUsers,
  getEmployeesAsUsers,
  getOrganization,
  key,
  partnerNamesById,
  partnersNotDeleted,
  toContactPersonViewModel,
  toPartnerViewModel,
  writeAccess,
} from 'src/store/selectors';
import type { State } from 'src/store/types.js';
import { LocalDate } from 'src/utilities/local-date.js';
import type { ApiClient } from 'src/store/backend';
import { sortBy } from 'lodash';
import { getPromoDataForEntity } from 'src/store/selectors/promo';
import type { ContactView } from '../../content/contacts/contact-view';
import type { ContactViewEditItem } from '../../content/contacts/contact-view-edit-item';
import type { ListSectionItemInput } from '../../library/lists/listSectionItemInput';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import type { PartnerView } from '../../content/partners/partner-view';
import type { PartnerViewEditItem } from '../../content/partners/partner-view-edit-item';
import { assertIsDefined } from '../../assertIsDefined';
import { assertEntityFound } from './entity-not-found';
import { toAttachmentItems } from '../to-attachment-items';
import { pagesAsSelectTagOptions } from '../pages-as-select-tag-options';
import { fetchDraft } from '../fetch-draft';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';
import { pageIds } from 'src/store/selectors/pages/pageIds';

function hasAssociatedComputers(uuid: string, state: State) {
  const computers = computersNotDeleted(state);
  return computers.find((c) => c.serviceProvider === uuid) !== undefined;
}

export function buildPartnerView(
  api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): PartnerView {
  const organization = getOrganization(state);
  assertIsDefined(organization);

  const item = organization.partnersById[uuid];
  assertEntityFound(item, 'partners', uuid);

  const entity = toPartnerViewModel(item, organization);
  const href = currentParent + '/partners/' + uuid;

  const current = currentEmployeeUuid(state);

  const contacts: ListSectionItemInput[] = contactsNotDeleted(state)
    .filter((c) => c.partnerUuid === uuid)
    .map((c) => ({
      href: href + '/contacts/' + c.uuid,
      label: c.firstName + ' ' + c.lastName,
      accessible: true,
      hasDraft: c.hasDraft,
    }));

  const contracts: ListSectionItemInput[] = contractsNotDeleted(state)
    .filter((c) => c.partners?.includes(uuid))
    .map((c) => ({
      href: href + '/contracts/' + c.uuid,
      label: c.name ?? '',
      mainIcon: c.classification !== 'NONE' ? 'lock' : undefined,
      accessible: c.classification === 'NONE' || (current !== undefined && (c.accessControl ?? []).includes(current)),
      hasDraft: c.hasDraft,
    }));

  return {
    ...commonData,
    promoData: getPromoDataForEntity('partners', item, getFeatureStates(state)),
    docsForLinking: [],
    currentUserHasAccess: true,
    currentUserHasWriteAccess: writeAccess(state) && hasPartnerAccess(state, entity),

    uuid: uuid,
    isConfirmedEntity: entity.isConfirmedEntity,
    helpContent: entity.helpContent ?? '',
    href: href,
    pdfHref:
      BASE_PATH + '/organizations/' + organization.organizationId + '/partners/' + uuid + '.pdf?key=' + key(state),

    parentHref: currentParent,
    deleted: entity.deleted === true,
    type: 'partners',
    remoteAccess: entity.remoteAccess === true,
    notes: entity.notes ?? '',
    url: entity.url ?? '',
    address: entity.address ?? '',
    physicalAccess: entity.physicalAccess === true,
    phone: entity.phone ?? '',
    email: entity.email ?? '',
    industry: entity.industry ?? '',
    name: entity.name ?? '',
    attachments: toAttachmentItems(api, 'partners', entity.uuid, entity.attachments),
    contacts: sortBy(contacts, [(item) => item.label]),
    contracts: sortBy(contracts, [(item) => item.label]),
    featureStates: getFeatureStates(state),
    availablePages: pagesAsSelectTagOptions(state),
    pages: (entity.pages ?? []).map((x) => '' + x),
    today: LocalDate.fromString(state.today),
    deletable: !hasAssociatedComputers(uuid, state),
    hasDraft: entity.hasDraft,
    fetchDraft: async () => {
      const command = await fetchDraft(api, 'partners', uuid, organization.organizationId);
      return command.draft as PartnerViewEditItem;
    },
    unchangedTemplate: entity.unchangedTemplate ?? false,
  };
}

function hasPartnerAccess(state: State, partner: PartnerViewModel): boolean {
  assertIsDefined(state.username);
  const organization = getOrganization(state);
  assertIsDefined(organization);

  // Check if this is a BHT partner (PlussBHT or MDCO)
  const isBhtPartner = partner.name === 'PlussBHT' || partner.name === 'MDCO';

  if (isBhtPartner) {
    // Allow BHT partner employees to always edit
    if (partner.name === 'PlussBHT' && state.username.endsWith('@plussbht.no')) {
      return true;
    }
    if (partner.name === 'MDCO' && state.username.endsWith('@mdco.no')) {
      return true;
    }

    // Allow edit for organization 2108 (special case)
    if (organization.organizationId === 2108) {
      return true;
    }

    // Allow edit if organization does NOT have HEALTHCARE feature
    const hasHealthcare = organization.features?.includes('HEALTHCARE') ?? false;
    if (!hasHealthcare) {
      return true;
    }

    // Otherwise, deny access (only BHT employees can edit for orgs with HEALTHCARE)
    return false;
  }

  // All other partners are freely editable
  return true;
}

function isEmergencyContent(partnerUuid: string, partners: PartnerViewModel[]) {
  const partner = partners.find((p) => p.uuid === partnerUuid);
  if (partner && partner.pages) {
    return partner.pages.includes(pageIds.emergency);
  }
}

export function buildContactView(
  api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): ContactView {
  const organization = getOrganization(state);
  assertIsDefined(organization);

  const item = organization.contactsById[uuid];
  assertEntityFound(item, 'contacts', uuid);

  const entity = toContactPersonViewModel(item);

  const partnerEntity = partnersNotDeleted(state).find((p) => p.uuid === entity.partnerUuid);
  assertIsDefined(partnerEntity);
  return {
    ...commonData,
    docsForLinking: [],
    currentUserHasAccess: true,
    currentUserHasWriteAccess: writeAccess(state) && hasPartnerAccess(state, partnerEntity),

    uuid: uuid,
    isConfirmedEntity: entity.isConfirmedEntity,
    helpContent: '',
    href: currentParent + '/contacts/' + uuid,
    pdfHref:
      BASE_PATH + '/organizations/' + organization.organizationId + '/contacts/' + uuid + '.pdf?key=' + key(state),

    parentHref: currentParent,
    deleted: entity.deleted,
    type: 'contacts',
    name: entity.firstName + ' ' + entity.lastName,
    attachments: toAttachmentItems(api, 'contacts', entity.uuid, entity.attachments, entity.partnerUuid),
    notes: entity.notes ?? '',
    accessLevel: entity.accessLevel ?? 'NONE',
    email: entity.email ?? '',
    telephone: entity.telephone ?? '',
    mobilePhone: entity.mobilePhone ?? '',
    partnerName: partnerNamesById(state)[entity.partnerUuid] ?? '',
    parentEntityId: entity.partnerUuid ?? '',
    lastName: entity.lastName,
    firstName: entity.firstName,
    personalIdentifier: entity.personalIdentifier,
    today: LocalDate.fromString(state.today),
    accessExpires: entity.accessExpires ?? '',
    deletable: true,
    hasDraft: entity.hasDraft,
    fetchDraft: async () => {
      const command = await fetchDraft(api, 'contacts', uuid, organization.organizationId);
      return command.draft as ContactViewEditItem;
    },
    featureStates: getFeatureStates(state),
    partnerUuid: entity.partnerUuid,
    partners: partnersNotDeleted(state).map((p) => {
      return { value: p.uuid, text: p.name };
    }),
    unavailableEmails: [
      ...getEmployeesAsUsers(state).map((e) => e.email ?? ''),
      ...getContactsAsUsers(state)
        .filter((c) => c.uuid !== uuid)
        .map((e) => e.email ?? ''),
    ].filter((e) => e !== ''),
    emergencyContent: isEmergencyContent(entity.partnerUuid, partnersNotDeleted(state)),
  };
}
